import { LuArrowUpRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrencyShort, formatPercent } from 'utils';
import { ALLOCATION_SEGMENT_COLORS } from 'views/household/asset-allocation-presentation';
import type { AssetAllocation } from 'views/household/holdings';

interface CurrentAllocationCardProps {
    allocation: AssetAllocation;
    isLoading: boolean;
    isError: boolean;
    isUnavailable?: boolean;
    allocationName?: string;
    isAllocationNameError?: boolean;
    allocationPath: string;
}

export const CurrentAllocationCard = ({
    allocation,
    isLoading,
    isError,
    isUnavailable = false,
    allocationName,
    isAllocationNameError = false,
    allocationPath,
}: CurrentAllocationCardProps) => {
    const { total, segments } = allocation;
    const statusMessage = isLoading
        ? 'Loading allocation…'
        : isError
          ? 'Unable to load allocation'
          : isUnavailable
            ? 'Allocation is available once the account is linked to a brokerage account'
            : segments.length === 0
              ? 'No allocation data available'
              : undefined;

    return (
        <Card className="border-border rounded-2xl bg-white p-4 lg:p-6">
            <div className="flex items-center justify-between gap-4">
                <h2 className="font-heading text-cocoa-1000 text-xl">Current Allocation</h2>
                <Button asChild variant="outline" size="icon" className="shrink-0 rounded-lg">
                    <Link to={allocationPath} aria-label="View target allocation">
                        <LuArrowUpRight className="h-5 w-5" />
                    </Link>
                </Button>
            </div>
            <div className="mt-5">
                <div className="text-cocoa-500 text-[10px] font-medium tracking-wider uppercase">Model portfolio</div>
                <div className="text-cocoa-1000 mt-2 text-sm font-medium">
                    {isAllocationNameError ? (
                        'Unable to load model portfolio'
                    ) : allocationName ? (
                        allocationName
                    ) : (
                        <Link to={allocationPath} className="text-teal-700 hover:underline">
                            Add a Target Allocation
                        </Link>
                    )}
                </div>
            </div>
            {isLoading ? (
                <div className="bg-cocoa-100 mt-5 h-2 animate-pulse rounded-full" />
            ) : (
                <div className="bg-cocoa-100 mt-5 flex h-2 overflow-hidden rounded-full">
                    {segments.map((segment) => (
                        <div
                            key={segment.key}
                            className={ALLOCATION_SEGMENT_COLORS[segment.key]}
                            style={{ width: `${(segment.amount / total) * 100}%` }}
                        />
                    ))}
                </div>
            )}
            <div className="mt-3 overflow-x-auto">
                <Table className="text-xs">
                    <TableHeader>
                        <TableRow className="hover:bg-transparent">
                            <TableHead className="text-cocoa-500 h-auto min-w-32 py-2 text-[10px] tracking-wider uppercase">
                                Asset class
                            </TableHead>
                            {['Current $', 'Current %'].map((heading) => (
                                <TableHead
                                    key={heading}
                                    className="text-cocoa-500 h-auto py-2 text-right text-[10px] tracking-wider whitespace-nowrap uppercase"
                                >
                                    {heading}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {statusMessage ? (
                            <TableRow>
                                <TableCell colSpan={3} className="text-cocoa-400 py-5 text-center">
                                    {statusMessage}
                                </TableCell>
                            </TableRow>
                        ) : (
                            segments.map((segment) => (
                                <TableRow key={segment.key}>
                                    <TableCell className="text-cocoa-900 py-3">
                                        <div className="flex items-center gap-3 whitespace-nowrap">
                                            <span
                                                className={`h-3 w-3 shrink-0 rounded-sm ${ALLOCATION_SEGMENT_COLORS[segment.key]}`}
                                            />
                                            {segment.key === 'cash' ? 'Cash' : segment.label}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-cocoa-1000 py-3 text-right font-medium tabular-nums">
                                        {formatCurrencyShort(segment.amount)}
                                    </TableCell>
                                    <TableCell className="text-cocoa-1000 py-3 text-right tabular-nums">
                                        {formatPercent((segment.amount / total) * 100, 1)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
};
